import toast from 'react-hot-toast';
import type { RecordModel } from 'pocketbase';
import pb from '@libs/pocketbase';
import type { SuperAdminRecord, SuperAdminFormValues, SuperAdminFilters, SuperAdminUpdateValues } from './types';

const collection = () => pb.collection('super_admins');

const sanitizeFilter = (filters?: SuperAdminFilters) => {
  if (!filters) return undefined;
  const clauses: string[] = [];

  if (filters.search) {
    const escaped = filters.search.replace(/"/g, '\\"');
    clauses.push(`(email~"${escaped}" || full_name~"${escaped}" || user_name~"${escaped}")`);
  }

  if (filters.status && filters.status !== 'all') {
    clauses.push(`status = "${filters.status}"`);
  }

  if (clauses.length === 0) return undefined;
  return clauses.join(' && ');
};

const mapRecord = (record: RecordModel): SuperAdminRecord => ({
  id: record.id,
  email: (record as unknown as { email: string }).email,
  user_name: (record as unknown as { user_name?: string }).user_name,
  full_name: (record as unknown as { full_name?: string }).full_name,
  phone: (record as unknown as { phone?: string }).phone,
  status: (record as unknown as { status?: 'active' | 'inactive' }).status ?? 'active',
  verified: (record as unknown as { verified?: boolean }).verified ?? false,
  is_deleted: (record as unknown as { is_deleted?: boolean }).is_deleted ?? false,
  created_by: (record as unknown as { created_by?: string }).created_by,
  updated_by: (record as unknown as { updated_by?: string }).updated_by,
  created_at: (record as unknown as { created_at?: string }).created_at ?? '',
  updated_at: (record as unknown as { updated_at?: string }).updated_at ?? '',
});

const generatePassword = () => {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz0123456789';
  if (typeof crypto !== 'undefined' && 'getRandomValues' in crypto) {
    const array = new Uint32Array(10);
    crypto.getRandomValues(array);
    return Array.from(array, (value) => chars[value % chars.length]).join('');
  }
  return Array.from({ length: 10 }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
};

export const superAdminApi = {
  async list(filters?: SuperAdminFilters): Promise<SuperAdminRecord[]> {
    const filter = sanitizeFilter(filters);
    const options = filter ? { filter } : undefined;
    const page = await collection().getList(1, 50, options);
    return page.items
      .map((item) => mapRecord(item as RecordModel))
      .sort((a, b) => {
        const timeA = Date.parse(a.created_at ?? '');
        const timeB = Date.parse(b.created_at ?? '');
        return (Number.isNaN(timeB) ? 0 : timeB) - (Number.isNaN(timeA) ? 0 : timeA);
      });
  },

  async summary(filters?: SuperAdminFilters) {
    const records = await this.list(filters);
    const total = records.length;
    const active = records.filter((item) => item.status === 'active').length;
    const inactive = total - active;
    const verified = records.filter((item) => item.verified).length;

    return {
      total,
      active,
      inactive,
      verified,
    };
  },

  async create(payload: SuperAdminFormValues) {
    const password = payload.password || generatePassword();

    // Prepare payload: trim strings, drop empty strings/undefined
    const trimOrUndefined = (value?: string) => {
      if (typeof value !== 'string') return value;
      const trimmed = value.trim();
      return trimmed.length > 0 ? trimmed : undefined;
    };

    const body: Record<string, unknown> = {
      email: payload.email.trim(),
      password,
      passwordConfirm: password,
      user_name: trimOrUndefined(payload.user_name),
      full_name: trimOrUndefined(payload.full_name),
      phone: trimOrUndefined(payload.phone),
      status: payload.status ?? 'active',
      // Ensure email is visible to clients; PB defaults to hidden when false
      emailVisibility: true,
    };

    // Remove undefined keys
    Object.keys(body).forEach((k) => (body as Record<string, unknown>)[k] === undefined && delete (body as Record<string, unknown>)[k]);

    const record = await collection().create(body);

    // Optional invite: send verification email if requested (silently handle errors)
    if (payload.sendInvite) {
      try {
        await collection().requestVerification(payload.email);
      } catch {
        // Non-blocking: silently handle email verification errors
      }
    }

    toast.success('Tạo Super Admin thành công');
    return mapRecord(record);
  },

  async update(id: string, payload: SuperAdminUpdateValues) {
    const record = await collection().update(id, payload);
    toast.success('Cập nhật thông tin thành công');
    return mapRecord(record);
  },

  async toggleStatus(id: string, nextStatus: 'active' | 'inactive') {
    const record = await collection().update(id, { status: nextStatus, is_deleted: nextStatus === 'inactive' });
    toast.success(nextStatus === 'active' ? 'Đã kích hoạt tài khoản' : 'Đã vô hiệu hóa tài khoản');
    return mapRecord(record);
  },

  async resetPassword(email: string) {
    await collection().requestPasswordReset(email);
    toast.success('Đã gửi email đặt lại mật khẩu');
  },

  async remove(id: string) {
    await collection().delete(id);
    toast.success('Đã xóa Super Admin');
  },
};